import { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import getModelType from "../../utils/getModelType";
import JSZip from "jszip";

const FileModelViewer = ({ file }) => {
  const mountRef = useRef(null);
  const [modelInfo, setModelInfo] = useState({
    model: null,
    type: null,
    textures: {},
  });

  useEffect(() => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (e) => {
      const arrayBuffer = e.target.result;
      const uint8Array = new Uint8Array(arrayBuffer);
      const isZip = file.name.toLowerCase().endsWith(".zip");

      if (isZip) {
        const zip = await JSZip.loadAsync(arrayBuffer);
        const files = Object.keys(zip.files);
        const textures = {};

        let modelFile = null;

        for (const name of files) {
          const lower = name.toLowerCase();
          if (lower.endsWith(".obj") || lower.endsWith(".fbx")) {
            modelFile = name;
            break;
          }
        }

        if (!modelFile) {
          console.error("No supported model (.obj or .fbx) in ZIP.");
          return;
        }

        const modelBlob = await zip.files[modelFile].async("blob");
        const modelUrl = URL.createObjectURL(modelBlob);

        for (const name of files) {
          const lower = name.toLowerCase();
          if (/\.(tga)$/.test(lower)) {
            const blob = await zip.files[name].async("blob");
            textures[name] = URL.createObjectURL(blob);
          }
        }

        const modelType = modelFile.toLowerCase().endsWith(".fbx")
          ? "fbx"
          : "obj";

        if (modelType === "obj") {
          const text = await modelBlob.text();
          const obj = new OBJLoader().parse(text);
          setModelInfo({ model: obj, type: "obj", textures });
        } else {
          setModelInfo({ model: modelUrl, type: "fbx", textures });
        }

        return;
      }

      const modelType = getModelType(uint8Array);

      if (modelType === "obj") {
        const text = new TextDecoder().decode(uint8Array);
        const obj = new OBJLoader().parse(text);
        setModelInfo({ model: obj, type: "obj" });
      } else if (modelType === "stl") {
        const geometry = new STLLoader().parse(arrayBuffer);
        const material = new THREE.MeshStandardMaterial({ color: 0x999999 });
        const mesh = new THREE.Mesh(geometry, material);
        setModelInfo({ model: mesh, type: "stl" });
      } else {
        const blobUrl = URL.createObjectURL(new Blob([arrayBuffer]));
        setModelInfo({ model: blobUrl, type: modelType });
      }
    };

    reader.readAsArrayBuffer(file);
  }, [file]);

  useEffect(() => {
    if (!mountRef.current || !modelInfo.model) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(
      mountRef.current.clientWidth,
      mountRef.current.clientHeight
    );
    mountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5).normalize();
    scene.add(directionalLight);

    const fitModelToScene = (model) => {
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      model.position.sub(center);

      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = camera.fov * (Math.PI / 180);
      const cameraZ = maxDim / (2 * Math.tan(fov / 2));

      camera.position.set(0, 0, cameraZ * 1.5);
      camera.near = maxDim / 100;
      camera.far = maxDim * 100;
      camera.updateProjectionMatrix();

      controls.target.set(0, 0, 0);
      controls.update();
    };

    const applyTexturesToModel = (model, textures) => {
      let hasMaterials = false;
      const textureLoader = new THREE.TextureLoader();
      const loadedTextures = textures.map((url) => textureLoader.load(url));

      model.traverse((child) => {
        if (child.isMesh) {
          hasMaterials = true;
          if (loadedTextures.length > 0) {
            child.material.map =
              loadedTextures[child.materialIndex] || loadedTextures[0];
          } else {
            child.material = new THREE.MeshStandardMaterial({
              color: 0xffffff,
            });
          }

          child.material.needsUpdate = true;
        }
      });

      if (!hasMaterials) {
        console.warn("No materials found, setting background color.");
        scene.background = new THREE.Color(0x333333);
      } else {
        scene.background = null;
      }
    };

    const loadModel = () => {
      if (modelInfo.type === "glb") {
        const loader = new GLTFLoader();
        loader.load(modelInfo.model, (gltf) => {
          fitModelToScene(gltf.scene);
          scene.add(gltf.scene);
          animate();
        });
      } else if (modelInfo.type === "fbx") {
        const loader = new FBXLoader();
        loader.load(modelInfo.model, (fbx) => {
          applyTexturesToModel(fbx, []);
          fitModelToScene(fbx);
          scene.add(fbx);
          animate();
        });
      } else if (modelInfo.type === "obj" || modelInfo.type === "stl") {
        fitModelToScene(modelInfo.model);
        scene.add(modelInfo.model);
        animate();
      }
    };

    let isAnimating = true;
    const animate = () => {
      if (!isAnimating) return;
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    loadModel();

    return () => {
      isAnimating = false;
      controls.dispose();
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [modelInfo]);

  return (
    <div
      style={{
        width: "100%",
        height: "400px",
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

export default FileModelViewer;
